// =============================================================================
// CustomSizeModal — enter a bespoke mattress width × length and get the price
// derived from the model's standard size ladder (see backend custom-size.ts).
// Used by the New Order product picker and the Catalogue detail page. The caller
// decides what to do with the result (add an order line, or add a cart item).
// =============================================================================
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CUSTOM_SIZE_MIN_CM, CUSTOM_SIZE_MAX_CM } from '@kalachand/shared';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';

interface ModelDetail {
  id: string;
  name: string;
  colorOptions: string[];
  sideColorOptions: string[];
  fabricOptions: string[];
  densityOptions: string[];
  allowsCustomSize: boolean;
  category: { deliveryRequired: boolean; fulfillmentStrategy: string };
}
interface CustomPrice {
  widthCm: number;
  lengthCm: number;
  matchedVariantId: string;
  matchedSize: string;
  unitPrice: string;
  vatRate: string;
  cappedAtLargest: boolean;
  // Special imported dimension surcharge (T&C §8): 0 for local custom sizes.
  surchargePct: number;
  preSurchargePrice: string;
  // Set when the matched standard size is on promo — the custom size follows it.
  promoApplied: boolean;
  matchedBasePrice: string;
}

// What the caller receives once a valid custom size is confirmed.
export interface CustomSizeResult {
  modelName: string;
  widthCm: number;
  lengthCm: number;
  matchedVariantId: string;
  matchedSize: string;
  unitPrice: number; // VAT-inclusive
  vatRate: number;
  colorOptions: string[];
  sideColorOptions: string[];
  fabricOptions: string[];
  densityOptions: string[];
  // The priced finish the custom size was quoted at — mirrors the selection made
  // on the product page. Threaded onto the order line's selected* fields so the
  // server recompute (which narrows the size ladder by finish) lands on the same
  // price we previewed here.
  selectedFabric: string | null;
  selectedColor: string | null;
  selectedSideColor: string | null;
  selectedDensity: string | null;
  deliveryRequired: boolean;
}

export function CustomSizeModal({
  modelId,
  fallbackName,
  fabric = null,
  color = null,
  sideColor = null,
  density = null,
  onClose,
  onConfirm,
}: {
  modelId: string;
  fallbackName?: string;
  // The priced finish selected on the product page. The custom size follows the
  // SAME selection — it prices against that finish's standard-size ladder (e.g.
  // a White variant costs more than a Blue one) rather than asking again.
  fabric?: string | null;
  color?: string | null;
  sideColor?: string | null;
  density?: string | null;
  onClose: () => void;
  onConfirm: (r: CustomSizeResult) => void;
}): JSX.Element {
  const [w, setW] = useState('');
  const [l, setL] = useState('');
  // Debounce the dimensions so we don't price on every keystroke.
  const [dw, setDw] = useState('');
  const [dl, setDl] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDw(w); setDl(l); }, 350);
    return () => clearTimeout(t);
  }, [w, l]);

  const modelQ = useQuery({
    queryKey: ['product-model', modelId],
    queryFn: () => apiGet<ModelDetail>(`/product-models/${modelId}`),
  });
  const model = modelQ.data;
  const ineligible = !!model && !model.allowsCustomSize;

  const wn = Number(dw);
  const ln = Number(dl);
  const validDims =
    Number.isInteger(wn) && Number.isInteger(ln) &&
    wn >= CUSTOM_SIZE_MIN_CM && wn <= CUSTOM_SIZE_MAX_CM &&
    ln >= CUSTOM_SIZE_MIN_CM && ln <= CUSTOM_SIZE_MAX_CM;

  // Price against the finish selected on the product page — the same colour /
  // fabric / etc. the customer is looking at, so the custom quote matches it.
  const priceQ = useQuery({
    queryKey: ['custom-size-price', modelId, dw, dl, color, fabric, sideColor, density],
    queryFn: () => apiPost<CustomPrice>(`/product-models/${modelId}/custom-size/price`, {
      widthCm: wn, lengthCm: ln, color, fabric, sideColor, density,
    }),
    enabled: validDims && !!model?.allowsCustomSize,
  });
  const price = priceQ.data;

  // Finish carried onto the line, in product-page priority order, for the summary.
  const finish = [color, fabric, sideColor, density].filter((x): x is string => !!x);

  const handleAdd = (): void => {
    if (!model || !price) return;
    onConfirm({
      modelName: model.name,
      widthCm: price.widthCm,
      lengthCm: price.lengthCm,
      matchedVariantId: price.matchedVariantId,
      matchedSize: price.matchedSize,
      unitPrice: Number(price.unitPrice),
      vatRate: Number(price.vatRate),
      colorOptions: model.colorOptions,
      sideColorOptions: model.sideColorOptions,
      fabricOptions: model.fabricOptions,
      densityOptions: model.densityOptions,
      selectedColor: color,
      selectedFabric: fabric,
      selectedSideColor: sideColor,
      selectedDensity: density,
      deliveryRequired: model.category.deliveryRequired,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <Card title="Custom mattress size" subtitle={model?.name ?? fallbackName}>
          {modelQ.isLoading ? (
            <p className="text-sm text-ink-3">Loading…</p>
          ) : ineligible ? (
            <p className="text-sm text-negative">
              This model can’t be ordered in a custom size — it needs at least two standard width×length sizes to price from.
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-ink-3">
                Enter the bespoke dimensions in centimetres. The price is taken from the next standard size up by area.
              </p>
              {model?.category.fulfillmentStrategy === 'STOCK_AT_LOCATION' && (
                <div className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-ink-2">
                  <strong className="text-warning">Special import.</strong> A custom imported mattress is brought in to order — it takes longer than a stock item, must be <strong>paid in full in advance</strong>, and is non-refundable. It won’t move to delivery until the import arrives.
                </div>
              )}
              <div className="flex items-end gap-3">
                <label className="flex-1 text-xs text-ink-3">
                  Width (cm)
                  <input
                    type="number" min={CUSTOM_SIZE_MIN_CM} max={CUSTOM_SIZE_MAX_CM} value={w}
                    onChange={(e) => setW(e.target.value)} className="kc-input w-full mt-1 kc-mono" autoFocus
                  />
                </label>
                <span className="pb-2 text-ink-4">×</span>
                <label className="flex-1 text-xs text-ink-3">
                  Length (cm)
                  <input
                    type="number" min={CUSTOM_SIZE_MIN_CM} max={CUSTOM_SIZE_MAX_CM} value={l}
                    onChange={(e) => setL(e.target.value)} className="kc-input w-full mt-1 kc-mono"
                  />
                </label>
              </div>

              {w !== '' && l !== '' && !validDims && (
                <p className="text-xs text-negative">
                  Each side must be a whole number between {CUSTOM_SIZE_MIN_CM} and {CUSTOM_SIZE_MAX_CM} cm.
                </p>
              )}

              {/* The finish follows the product-page selection — shown here so
                  it's clear which colour/fabric the custom price is quoted for. */}
              {finish.length > 0 && (
                <p className="text-[11px] text-ink-3">
                  Priced for <span className="text-ink">{finish.join(' · ')}</span>
                </p>
              )}

              {validDims && (
                <div className="rounded-md border border-rule bg-paper-2 px-4 py-3 text-sm min-h-[3.25rem] flex items-center">
                  {priceQ.isLoading ? (
                    <span className="text-ink-3">Pricing…</span>
                  ) : priceQ.isError ? (
                    <span className="text-negative">{getApiError(priceQ.error).message}</span>
                  ) : price ? (
                    <div className="flex items-center justify-between w-full">
                      <div className="text-xs text-ink-3">
                        Priced as <span className="text-ink kc-mono">{price.matchedSize}</span>
                        {price.cappedAtLargest && (
                          <span className="block text-warning mt-0.5">
                            Bigger than every standard size — charged at the largest size’s price.
                          </span>
                        )}
                        {price.promoApplied && (
                          <span className="block text-accent mt-0.5">
                            {price.matchedSize} is on promo — was <Money value={price.matchedBasePrice} className="line-through" />
                          </span>
                        )}
                        {price.surchargePct > 0 && (
                          <span className="block text-warning mt-0.5">
                            <Money value={price.preSurchargePrice} /> + {Math.round(price.surchargePct * 100)}% special import surcharge
                          </span>
                        )}
                      </div>
                      <Money value={price.unitPrice} className={cn('text-base font-medium shrink-0', price.promoApplied && 'text-accent')} />
                    </div>
                  ) : null}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button onClick={handleAdd} disabled={!price || priceQ.isLoading || priceQ.isError}>
                  Add to order
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
